import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Theme-aware palettes shared across the client tab UI.
// IMPORTANT: callers must pass `isVisuallyDark` (i.e. !useTheme().isDarkMode)
// because the CSS in this project uses an inverted naming convention where
// `[data-theme="light"]` actually styles the dark visual mode.
function getStatusPalette(isVisuallyDark) {
  return {
    success: isVisuallyDark
      ? {
          accent: "hsl(150 75% 68%)",
          accentStrong: "hsl(150 75% 75%)",
          bg: "linear-gradient(135deg, hsl(150 65% 40% / 0.32), hsl(150 65% 35% / 0.12))",
          border: "1px solid hsl(150 70% 55% / 0.5)",
          iconBg: "hsl(150 70% 50% / 0.28)",
          iconColor: "hsl(150 75% 75%)",
        }
      : {
          accent: "hsl(150 60% 32%)",
          accentStrong: "hsl(150 60% 26%)",
          bg: "linear-gradient(135deg, hsl(150 60% 45% / 0.16), hsl(150 60% 45% / 0.04))",
          border: "1px solid hsl(150 60% 45% / 0.4)",
          iconBg: "hsl(150 60% 45% / 0.22)",
          iconColor: "hsl(150 60% 30%)",
        },
    danger: isVisuallyDark
      ? { accent: "hsl(0 85% 72%)" }
      : { accent: "hsl(0 80% 45%)" },
    surface: isVisuallyDark
      ? {
          bg: "hsl(234 30% 18%)",
          border: "1px solid hsl(234 20% 28%)",
        }
      : {
          bg: "hsl(210 25% 97%)",
          border: "1px solid hsl(220 14% 90%)",
        },
    surfaceMuted: isVisuallyDark ? "hsl(234 25% 14%)" : "hsl(210 14% 96%)",
    cta: isVisuallyDark
      ? {
          bg: "linear-gradient(135deg, hsl(166 95% 32% / 0.32), hsl(166 95% 28% / 0.12))",
          border: "1px solid hsl(166 95% 50% / 0.45)",
        }
      : {
          bg: "linear-gradient(135deg, hsl(var(--main) / 0.12), hsl(var(--main) / 0.04))",
          border: "1px solid hsl(var(--main) / 0.3)",
        },
  };
}

const FAKE_PARTNER_DIRECTORY = {
  "NOMIO-0042-AM": "Արամ Մինասյան",
  "NOMIO-0015-AM": "Անի Բաղդասարյան",
  "NOMIO-0007-AM": "Դավիթ Հովհաննիսյան",
};

const CLIENT_STATES = [
  { id: "fresh", icon: "la-leaf", labelKey: "clientSetup.demoState.fresh" },
  { id: "via-link", icon: "la-link", labelKey: "clientSetup.demoState.viaLink" },
  { id: "code-saved", icon: "la-bookmark", labelKey: "clientSetup.demoState.codeSaved" },
  { id: "has-account", icon: "la-id-card-alt", labelKey: "clientSetup.demoState.hasAccount" },
];

const FAKE_ORGANIZATION = {
  name: "Արարատ Գրուպ ՍՊԸ",
  package: "Բիզնես",
  packagePrice: 49900,
  admin_username: "ararat_admin",
  dashboard_url: "https://dashboard.nomio.am",
  created_at: "2026-04-15",
  members: 14,
};

function ClientPlatformTab({ user, isDarkMode, onOpenWizard }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  // CSS uses inverted convention: `[data-theme="light"]` triggers dark
  // visuals, so visual dark mode is when isDarkMode is FALSE.
  const isVisuallyDark = !isDarkMode;
  const palette = useMemo(
    () => getStatusPalette(isVisuallyDark),
    [isVisuallyDark],
  );

  // URL-driven demo state for the team-lead presentation
  const clientState = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get("clientState");
    return CLIENT_STATES.some((s) => s.id === value) ? value : "fresh";
  }, [location.search]);

  const setClientState = (id) => {
    const params = new URLSearchParams(location.search);
    if (id === "fresh") {
      params.delete("clientState");
    } else {
      params.set("clientState", id);
    }
    const qs = params.toString();
    navigate(qs ? `${location.pathname}?${qs}` : location.pathname, {
      replace: false,
    });
  };

  // Derived attribution + account state based on demoState
  const stateData = useMemo(() => {
    switch (clientState) {
      case "via-link":
        return {
          hasPlatformAccount: false,
          attribution: {
            source: "link",
            code: "NOMIO-0042-AM",
            partner: FAKE_PARTNER_DIRECTORY["NOMIO-0042-AM"],
          },
        };
      case "code-saved":
        return {
          hasPlatformAccount: false,
          attribution: {
            source: "saved",
            code: "NOMIO-0015-AM",
            partner: FAKE_PARTNER_DIRECTORY["NOMIO-0015-AM"],
          },
        };
      case "has-account":
        return {
          hasPlatformAccount: true,
          attribution: {
            source: "saved",
            code: "NOMIO-0042-AM",
            partner: FAKE_PARTNER_DIRECTORY["NOMIO-0042-AM"],
          },
          organization: FAKE_ORGANIZATION,
        };
      default:
        return {
          hasPlatformAccount: false,
          attribution: { source: "none", code: "", partner: "" },
        };
    }
  }, [clientState]);

  return (
    <div className="d-flex flex-column gap-3">
      <WelcomeCard
        userName={user?.name || user?.first_name || ""}
        hasAccount={stateData.hasPlatformAccount}
        isVisuallyDark={isVisuallyDark}
        t={t}
      />

      <DemoStateSelector
        currentState={clientState}
        onChange={setClientState}
        isVisuallyDark={isVisuallyDark}
        t={t}
      />

      {!stateData.hasPlatformAccount && (
        <AttributionPanel
          attribution={stateData.attribution}
          palette={palette}
          onSavedLocally={(code, partner) =>
            navigate(
              `${location.pathname}?clientState=code-saved`,
              { replace: false },
            )
          }
          onRemove={() =>
            navigate(`${location.pathname}`, { replace: false })
          }
          t={t}
        />
      )}

      {stateData.hasPlatformAccount ? (
        <PlatformAccountCard
          organization={stateData.organization}
          attribution={stateData.attribution}
          lang={i18n.language}
          palette={palette}
          t={t}
        />
      ) : (
        <SetupCta
          palette={palette}
          onOpen={() =>
            onOpenWizard({
              attachedReferralCode: stateData.attribution.code || "",
              partnerName: stateData.attribution.partner || "",
              cameFromReferralLink:
                stateData.attribution.source === "link",
            })
          }
          t={t}
        />
      )}
    </div>
  );
}

/* =====================================================================
 * Welcome card
 * ===================================================================== */

function WelcomeCard({ userName, hasAccount, isVisuallyDark, t }) {
  const gradient = isVisuallyDark
    ? "linear-gradient(135deg, hsl(166 80% 38%), hsl(192 80% 40%))"
    : "linear-gradient(135deg, hsl(166 100% 41%), hsl(192 95% 45%))";
  return (
    <div
      className="rounded-4 p-4 text-white position-relative overflow-hidden"
      style={{
        background: gradient,
        boxShadow: isVisuallyDark
          ? "0 10px 32px hsl(166 80% 30% / 0.45)"
          : "0 10px 32px hsl(166 100% 41% / 0.25)",
      }}
    >
      <div className="d-flex flex-wrap align-items-start gap-3 justify-content-between">
        <div className="flex-grow-1">
          <span
            className="badge fw-600 rounded-pill px-3 py-1 mb-2"
            style={{
              backgroundColor: hasAccount
                ? "rgba(255,255,255,0.25)"
                : "rgba(0,0,0,0.18)",
              color: "#fff",
              fontSize: 11,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
          >
            <i
              className={`las ${
                hasAccount ? "la-check-circle" : "la-rocket"
              } me-1`}
            />
            {hasAccount
              ? t("clientSetup.welcome.hasAccountBadge")
              : t("clientSetup.welcome.noAccountBadge")}
          </span>
          <h4 className="font-24 fw-700 mb-1 text-white">
            {t("clientSetup.welcome.title", {
              name: userName || "👋",
            })}
          </h4>
          <p
            className="font-14 mb-0"
            style={{ color: "rgba(255,255,255,0.92)" }}
          >
            {t("clientSetup.welcome.subtitle")}
          </p>
        </div>
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
          style={{
            width: 60,
            height: 60,
            backgroundColor: "rgba(255,255,255,0.22)",
          }}
          aria-hidden="true"
        >
          <i
            className={`las ${hasAccount ? "la-th-large" : "la-user-plus"} text-white`}
            style={{ fontSize: "2rem" }}
          />
        </div>
      </div>
    </div>
  );
}

/* =====================================================================
 * Demo state selector (visible in preview mode for the team-lead demo)
 * ===================================================================== */

function DemoStateSelector({ currentState, onChange, isVisuallyDark, t }) {
  const palette = isVisuallyDark
    ? {
        bg: "linear-gradient(135deg, hsl(38 95% 30% / 0.35), hsl(38 95% 25% / 0.15))",
        border: "1px solid hsl(38 95% 55% / 0.5)",
        text: "hsl(38 95% 75%)",
      }
    : {
        bg: "linear-gradient(135deg, hsl(38 95% 60% / 0.18), hsl(38 95% 60% / 0.06))",
        border: "1px solid hsl(38 95% 55% / 0.4)",
        text: "hsl(38 80% 38%)",
      };

  return (
    <div
      className="rounded-3 p-3 d-flex flex-wrap align-items-center gap-2"
      style={{ background: palette.bg, border: palette.border }}
    >
      <span
        className="font-11 text-uppercase fw-700 d-flex align-items-center gap-1"
        style={{
          letterSpacing: "0.06em",
          color: palette.text,
        }}
      >
        <i className="las la-flask" />
        {t("clientSetup.demoState.label")}
      </span>
      <div className="d-flex flex-wrap gap-1">
        {CLIENT_STATES.map((s) => {
          const active = currentState === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onChange(s.id)}
              className="btn btn-sm rounded-pill px-3 py-1 font-12 fw-500"
              style={{
                backgroundColor: active
                  ? palette.text
                  : "transparent",
                color: active ? "#fff" : palette.text,
                border: `1px solid ${palette.text}`,
              }}
            >
              <i className={`las ${s.icon} me-1`} />
              {t(s.labelKey)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* =====================================================================
 * Attribution panel
 * ===================================================================== */

function AttributionPanel({ attribution, palette, onSavedLocally, onRemove, t }) {
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState("");
  const [validating, setValidating] = useState(false);
  const [validity, setValidity] = useState(null);

  // Live code validation
  useEffect(() => {
    const trimmed = (code || "").trim().toUpperCase();
    if (!trimmed) {
      setValidity(null);
      return;
    }
    setValidating(true);
    const timer = setTimeout(() => {
      const partner = FAKE_PARTNER_DIRECTORY[trimmed];
      setValidity(partner ? { ok: true, partner } : { ok: false });
      setValidating(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [code]);

  if (attribution.source === "link") {
    return (
      <AttributionCard
        variant="success"
        palette={palette}
        icon="la-link"
        title={t("clientSetup.attribution.viaLinkTitle")}
        description={t("clientSetup.attribution.viaLinkDescription", {
          partner: attribution.partner,
        })}
        code={attribution.code}
      />
    );
  }

  if (attribution.source === "saved") {
    return (
      <AttributionCard
        variant="success"
        palette={palette}
        icon="la-bookmark"
        title={t("clientSetup.attribution.savedCodeTitle")}
        description={t("clientSetup.attribution.savedCodeDescription", {
          code: attribution.code,
          partner: attribution.partner,
        })}
        code={attribution.code}
        onRemove={onRemove}
        removeLabel={t("clientSetup.attribution.remove")}
      />
    );
  }

  // source === "none" — show "+ Add referral code" expander
  if (!adding) {
    return (
      <div
        className="rounded-3 p-3 d-flex flex-wrap align-items-center justify-content-between gap-2"
        style={{
          backgroundColor: palette.surface.bg,
          border: palette.surface.border.replace("solid", "dashed"),
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <i
            className="las la-info-circle"
            style={{
              fontSize: "1.4rem",
              color: "hsl(var(--body-color))",
            }}
          />
          <span
            className="font-14"
            style={{ color: "hsl(var(--body-color))" }}
          >
            {t("clientSetup.attribution.noAttribution")}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="btn btn-outline-main pill fw-500 px-3 font-13"
        >
          {t("clientSetup.attribution.addCodeBtn")}
        </button>
      </div>
    );
  }

  return (
    <div
      className="rounded-3 p-3"
      style={{
        backgroundColor: palette.surface.bg,
        border: "1px solid hsl(var(--main) / 0.4)",
      }}
    >
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span
          className="font-13 fw-600"
          style={{ color: "hsl(var(--heading-color))" }}
        >
          <i className="las la-bookmark me-1" />
          {t("clientSetup.attribution.addCodeTitle")}
        </span>
        <button
          type="button"
          onClick={() => {
            setAdding(false);
            setCode("");
            setValidity(null);
          }}
          className="btn btn-sm bg-transparent border-0 p-0 font-13"
          style={{ color: "hsl(var(--body-color))" }}
        >
          <i className="las la-times" />
        </button>
      </div>
      <p
        className="font-12 mb-2"
        style={{ color: "hsl(var(--body-color))" }}
      >
        {t("clientSetup.attribution.addCodeHint")}
      </p>
      <div className="position-relative">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={t("clientSetup.attribution.addCodePlaceholder")}
          className="common-input border font-14 fw-600 text-uppercase"
          style={{ letterSpacing: "0.05em", paddingRight: 48 }}
          autoFocus
        />
        {validating && (
          <span
            className="position-absolute spinner-border spinner-border-sm"
            style={{
              right: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: "hsl(var(--main))",
            }}
            role="status"
            aria-hidden="true"
          />
        )}
      </div>
      {validity?.ok && !validating && (
        <div
          className="font-12 mt-2 fw-600"
          style={{ color: palette.success.accent }}
        >
          <i className="las la-check-circle me-1" />
          {t("clientSetup.attribution.valid", { partner: validity.partner })}
        </div>
      )}
      {validity?.ok === false && (
        <div
          className="font-12 mt-2"
          style={{ color: palette.danger.accent }}
        >
          <i className="las la-times-circle me-1" />
          {t("clientSetup.attribution.invalid")}
        </div>
      )}
      <div className="d-flex gap-2 mt-3">
        <button
          type="button"
          onClick={() => {
            if (validity?.ok) {
              onSavedLocally(code.trim().toUpperCase(), validity.partner);
              setAdding(false);
            }
          }}
          disabled={!validity?.ok}
          className="btn btn-main pill fw-500 px-3 font-13"
          style={{ opacity: validity?.ok ? 1 : 0.5 }}
        >
          <i className="las la-check me-1" />
          {t("clientSetup.attribution.save")}
        </button>
        <button
          type="button"
          onClick={() => {
            setAdding(false);
            setCode("");
            setValidity(null);
          }}
          className="btn btn-outline-main pill fw-500 px-3 font-13"
        >
          {t("clientSetup.attribution.cancel")}
        </button>
      </div>
    </div>
  );
}

function AttributionCard({
  variant,
  palette,
  icon,
  title,
  description,
  code,
  onRemove,
  removeLabel,
}) {
  const colors =
    variant === "success"
      ? {
          bg: palette.success.bg,
          border: palette.success.border,
          accent: palette.success.accent,
          accentStrong: palette.success.accentStrong,
          iconBg: palette.success.iconBg,
          iconColor: palette.success.iconColor,
        }
      : {
          bg: "transparent",
          border: palette.surface.border,
          accent: "hsl(var(--body-color))",
          accentStrong: "hsl(var(--heading-color))",
          iconBg: palette.surfaceMuted,
          iconColor: "hsl(var(--body-color))",
        };

  return (
    <div
      className="rounded-3 p-3 d-flex align-items-start gap-3"
      style={{ background: colors.bg, border: colors.border }}
    >
      <span
        className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
        style={{
          width: 44,
          height: 44,
          backgroundColor: colors.iconBg,
          color: colors.iconColor,
        }}
        aria-hidden="true"
      >
        <i className={`las ${icon}`} style={{ fontSize: "1.4rem" }} />
      </span>
      <div className="flex-grow-1">
        <div
          className="font-13 fw-700 mb-1"
          style={{ color: colors.accent, letterSpacing: "0.02em" }}
        >
          {title}
        </div>
        <div
          className="font-14"
          style={{ color: "hsl(var(--heading-color))" }}
        >
          {description}
        </div>
        {code && (
          <div
            className="font-12 mt-1 fw-600"
            style={{
              color: colors.accent,
              fontFamily: "monospace",
              letterSpacing: "0.04em",
            }}
          >
            {code}
          </div>
        )}
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="btn btn-sm rounded-pill px-3 fw-500 font-12"
          style={{
            backgroundColor: "transparent",
            color: palette.danger.accent,
            border: `1px solid ${palette.danger.accent}`,
          }}
        >
          <i className="las la-times me-1" />
          {removeLabel}
        </button>
      )}
    </div>
  );
}

/* =====================================================================
 * "Set up platform access" big CTA (when no platform account yet)
 * ===================================================================== */

function SetupCta({ palette, onOpen, t }) {
  return (
    <div
      className="rounded-4 p-4 d-flex flex-wrap align-items-center gap-3 justify-content-between"
      style={{
        background: palette.cta.bg,
        border: palette.cta.border,
      }}
    >
      <div className="flex-grow-1" style={{ maxWidth: 520 }}>
        <h5
          className="font-18 fw-700 mb-2"
          style={{ color: "hsl(var(--heading-color))" }}
        >
          <i
            className="las la-th-large me-2"
            style={{ color: "hsl(var(--main))" }}
          />
          {t("clientSetup.platformAccount.ctaPrimary")}
        </h5>
        <p
          className="font-14 mb-0"
          style={{ color: "hsl(var(--body-color))" }}
        >
          {t("clientSetup.platformAccount.intro")}
        </p>
      </div>
      <div className="d-flex flex-column align-items-end gap-1">
        <button
          type="button"
          onClick={onOpen}
          className="btn btn-main pill fw-500 px-4 d-flex align-items-center gap-2"
        >
          <i className="las la-rocket" />
          {t("clientSetup.platformAccount.ctaPrimary")}
          <i className="las la-arrow-right" />
        </button>
        <span
          className="font-12"
          style={{ color: "hsl(var(--body-color))" }}
        >
          {t("clientSetup.platformAccount.ctaSubtitle")}
        </span>
      </div>
    </div>
  );
}

/* =====================================================================
 * Platform account card (when user has admin platform set up)
 * ===================================================================== */

function PlatformAccountCard({ organization, attribution, lang, palette, t }) {
  const numLocale = lang === "hy" ? "hy-AM" : "en-US";
  return (
    <div className="d-flex flex-column gap-3">
      <div
        className="rounded-4 p-4"
        style={{
          backgroundColor: palette.surface.bg,
          border: palette.surface.border,
        }}
      >
        <div className="d-flex flex-wrap align-items-start gap-3 justify-content-between mb-3">
          <div>
            <span
              className="font-11 text-uppercase fw-700 d-block mb-1"
              style={{
                letterSpacing: "0.06em",
                color: "hsl(var(--main))",
              }}
            >
              {t("clientSetup.platformAccount.hasAccountTitle")}
            </span>
            <h4
              className="font-22 fw-700 mb-0"
              style={{ color: "hsl(var(--heading-color))" }}
            >
              {organization.name}
            </h4>
          </div>
          <a
            href={organization.dashboard_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-main pill fw-500 px-4"
          >
            <i className="las la-external-link-alt me-1" />
            {t("clientSetup.platformAccount.openPlatform")}
          </a>
        </div>

        <div className="row gx-3 gy-3">
          <OrgField
            icon="la-cube"
            label={t("clientSetup.platformAccount.fields.package")}
            value={
              <>
                {organization.package}
                <span
                  className="font-13 text-body ms-2"
                  style={{ fontWeight: 400 }}
                >
                  {new Intl.NumberFormat(numLocale).format(
                    organization.packagePrice,
                  )}{" "}
                  ֏
                </span>
              </>
            }
          />
          <OrgField
            icon="la-user-shield"
            label={t("clientSetup.platformAccount.fields.admin")}
            value={
              <span style={{ fontFamily: "monospace" }}>
                @{organization.admin_username}
              </span>
            }
          />
          <OrgField
            icon="la-users"
            label={t("clientSetup.platformAccount.fields.members")}
            value={`${organization.members}`}
          />
          <OrgField
            icon="la-calendar"
            label={t("clientSetup.platformAccount.fields.createdAt")}
            value={new Date(organization.created_at).toLocaleDateString(
              numLocale,
              {
                year: "numeric",
                month: "short",
                day: "2-digit",
              },
            )}
          />
        </div>

        {attribution.partner && (
          <div
            className="mt-3 pt-3 d-flex align-items-center gap-2 font-13"
            style={{
              borderTop: "1px solid hsl(var(--border-color))",
              color: "hsl(var(--body-color))",
            }}
          >
            <i
              className="las la-handshake"
              style={{ color: palette.success.accent }}
            />
            <span>
              Ձեր գործընկեր՝{" "}
              <b style={{ color: "hsl(var(--heading-color))" }}>
                {attribution.partner}
              </b>{" "}
              ({attribution.code})
            </span>
          </div>
        )}
      </div>

      <div className="row gy-2">
        <QuickAction
          icon="la-credit-card"
          label={t("clientSetup.platformAccount.manageSubscription")}
          palette={palette}
        />
        <QuickAction
          icon="la-user-plus"
          label={t("clientSetup.platformAccount.inviteTeam")}
          palette={palette}
        />
        <QuickAction
          icon="la-file-invoice"
          label={t("clientSetup.platformAccount.viewInvoices")}
          palette={palette}
        />
      </div>
    </div>
  );
}

function OrgField({ icon, label, value }) {
  return (
    <div className="col-md-6 d-flex align-items-start gap-2">
      <span
        className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
        style={{
          width: 36,
          height: 36,
          backgroundColor: "hsl(var(--main) / 0.12)",
          color: "hsl(var(--main))",
        }}
        aria-hidden="true"
      >
        <i className={`las ${icon}`} style={{ fontSize: "1.1rem" }} />
      </span>
      <div className="min-width-0">
        <div
          className="font-11 text-uppercase fw-700"
          style={{
            letterSpacing: "0.05em",
            color: "hsl(var(--body-color))",
          }}
        >
          {label}
        </div>
        <div
          className="font-15 fw-600"
          style={{ color: "hsl(var(--heading-color))" }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function QuickAction({ icon, label, palette }) {
  return (
    <div className="col-md-4">
      <button
        type="button"
        className="w-100 rounded-3 px-3 py-3 d-flex align-items-center gap-2 font-13 fw-500 text-start"
        style={{
          backgroundColor: palette.surface.bg,
          border: palette.surface.border,
          color: "hsl(var(--heading-color))",
        }}
      >
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
          style={{
            width: 32,
            height: 32,
            backgroundColor: "hsl(var(--main) / 0.16)",
            color: "hsl(var(--main))",
          }}
          aria-hidden="true"
        >
          <i className={`las ${icon}`} style={{ fontSize: "1rem" }} />
        </span>
        <span className="flex-grow-1">{label}</span>
        <i className="las la-arrow-right" />
      </button>
    </div>
  );
}

export default ClientPlatformTab;
